import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link as RouterLink, useLocation } from "react-router-dom";
import userService from "../services/userService";
import {
  getMissingUserFields,
  getMissingDepositoFields,
  PROFILE_UPDATED_EVENT,
} from "../utils/profileCompleteness";

// Banner persistente de "completá tus datos": aparece en toda la app logueada
// mientras falte algún campo obligatorio del perfil (información personal o
// depósito). No se puede cerrar a propósito — desaparece solo cuando los datos
// están completos. Patrón visual de PlanBanner.
const ProfileIncompleteBanner = () => {
  // null = todavía no se evaluó (no mostrar nada); {user: [], deposito: []} = evaluado.
  const [missing, setMissing] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const location = useLocation();

  const evaluate = useCallback(() => {
    const userId = Number(sessionStorage.getItem("User"));
    if (!userId) return;

    Promise.all([userService.getProfile(), userService.getDeposits()])
      .then(([profileRes, depositsRes]) => {
        const data = profileRes?.data;
        if (!data) return;
        const isSuperAdmin = userId === 1;
        setIsOwner(Boolean(data.is_owner) || isSuperAdmin);
        setMissing({
          user: getMissingUserFields({
            firstname: data.firstname,
            lastname: data.lastname,
            company: data.company?.name,
          }),
          deposito: getMissingDepositoFields(depositsRes?.data?.[0] || null),
        });
      })
      .catch(() => {
        // Si el perfil no se pudo cargar, no se muestra el banner (no
        // queremos un falso "datos incompletos" por un error de red).
      });
  }, []);

  // Se re-evalúa al montar, al guardar el perfil (evento) y al navegar.
  useEffect(() => {
    evaluate();
    window.addEventListener(PROFILE_UPDATED_EVENT, evaluate);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, evaluate);
  }, [evaluate, location.pathname]);

  if (!missing) return null;
  const parts = [];
  if (missing.user.length > 0) parts.push(`información personal (${missing.user.join(", ")})`);
  if (missing.deposito.length > 0) parts.push(`depósito (${missing.deposito.join(", ")})`);
  if (parts.length === 0) return null;

  const palette = { bg: "#eff6ff", border: "#bfdbfe", fg: "#1e40af", icon: "#2563eb" };

  const mensaje = isOwner
    ? `Completá los datos de tu cuenta para operar sin problemas: falta ${parts.join(" y ")}.`
    : `Faltan datos de la cuenta: ${parts.join(" y ")}. Pedile al dueño de la cuenta que los complete.`;

  const enProfile = location.pathname === "/profile";

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 2, md: 3 },
        py: 1.25,
        backgroundColor: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
      }}
    >
      <InfoOutlinedIcon sx={{ color: palette.icon, fontSize: 22, flexShrink: 0 }} />
      <Typography sx={{ flexGrow: 1, fontSize: "0.85rem", fontWeight: 500, color: palette.fg }}>
        {mensaje}
      </Typography>
      {isOwner && !enProfile && (
        <Button
          component={RouterLink}
          to="/profile"
          variant="contained"
          disableElevation
          sx={{
            flexShrink: 0,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8rem",
            borderRadius: "8px",
            px: 2,
            backgroundColor: palette.fg,
            "&:hover": { backgroundColor: palette.icon },
          }}
        >
          Completar datos
        </Button>
      )}
    </Box>
  );
};

export default ProfileIncompleteBanner;
